import * as React from 'react'
import {
  clearAdminSelection,
  requestAdminRemove,
  subscribeToAdminSelectedCount,
} from '@/modules/shared/stores/adminSelectionStore'
import {
  withLocalizeProps,
  type LocalizeProps,
} from '@/modules/shared/context/localize/LocalizeContext'

import {
  SubNavigationBar,
  SubNavigationButton,
  AdminIcon,
} from '@/modules/shared/components'
import { TabContent, TabPane } from '@/modules/shared/components/Tabs'
import { ThemeModeToggle } from '@/modules/shared/components/ThemeModeToggle'

import FlexView from '@/modules/shared/components/FlexView'

import { AdminCompaniesPage } from '@/modules/adminPanel/companies/AdminCompaniesPage'
import { AdminDemoCompaniesPage } from '@/modules/adminPanel/demoCompanies/AdminDemoCompaniesPage'
import { AdminUsersPage } from '@/modules/adminPanel/users/AdminUsersPage'
import { AdminServersPage } from '@/modules/adminPanel/servers/AdminServersPage'
import { AdminDatabasesPage } from '@/modules/adminPanel/databases/AdminDatabasesPage'
import { AdminRolesPage } from '@/modules/adminPanel/roles/AdminRolesPage'
import { AdminCompanyGroupsPage } from '@/modules/adminPanel/companyGroups/AdminCompanyGroupsPage'
import Reports from 'app/containers/administration/reportsContainer'
import { AdminTasksPage } from '@/modules/adminPanel/tasks/AdminTasksPage'
import { AdminMessagesPage } from '@/modules/adminPanel/messages/AdminMessagesPage'
import { SystemTab } from './SystemTab'
import { PaymentCheckTab } from './PaymentCheckTab'
import { ManualBillingTab } from './ManualBillingTab'
import { Przelewy24BillingTab } from './Przelewy24BillingTab'
import { ServiceAccountTab } from './ServiceAccountTab'
import { CompanyNipMismatchBanner } from './CompanyNipMismatchBanner'

import {
  isAdmin,
  isCompanyAdmin,
  isDemoCompanyAdmin,
  isPaymentAdmin,
} from 'app/services/local/roleService'
import { CompanyDetailsModel } from 'app/models/companyDetailsModel'
import { CompanyType } from 'app/constants/companyType'
import { ROUTES } from 'app/constants/routes'
import { generatePath } from 'react-router-dom'

import {
  withConfirm,
  WithConfirmProps,
} from '@/modules/shared/hocs/withConfirm'
import { RouteComponentProps, withRouter } from '@/app/navigation/withRouter'

export type AdministrationPanelStateProps = {
  isLoading: boolean
}

export type AdministrationPanelDispatchProps = {
  getCompany: (companyId: string) => Promise<CompanyDetailsModel>
}

export type AdministrationPanelOwnProps = {}

type AdministrationPanelProps = AdministrationPanelStateProps &
  AdministrationPanelDispatchProps &
  AdministrationPanelOwnProps &
  WithConfirmProps

type State = {
  activeTab: string
  displayActionsBar: boolean
  displayCompaniesTab: boolean
  currentCompany: CompanyDetailsModel
}

type MatchParams = {
  tab: string
  companyId: string
}

const TABS = {
  Company: 'company',
  User: 'user',
  Server: 'server',
  Database: 'database',
  Role: 'role',
  CompanyGroups: 'companyGroup',
  Reports: 'report',
  Tasks: 'tasks',
  Messages: 'messages',
  Demo: 'demo',
  System: 'system',
  ManualBilling: 'manualBilling',
  Przelewy24: 'przelewy24',
  PaymentCheck: 'paymentCheck',
  ServiceAccount: 'serviceAccount',
}

class AdministrationPanel extends React.Component<
  AdministrationPanelProps &
    RouteComponentProps<MatchParams> &
    LocalizeProps,
  State
> {
  constructor(
    props: AdministrationPanelProps &
      RouteComponentProps<MatchParams> &
      LocalizeProps,
  ) {
    super(props)

    this.toggle = this.toggle.bind(this)
    this.state = {
      activeTab: null,
      displayActionsBar: false,
      displayCompaniesTab: false,
      currentCompany: null,
    }
  }

  private unsubscribeSelection: (() => void) | null = null

  async componentDidMount() {
    // Zakładki zgłaszają zaznaczenie przez store; panel nie zna już ich instancji.
    this.unsubscribeSelection = subscribeToAdminSelectedCount((count) =>
      this.setState({ displayActionsBar: count > 0 }),
    )

    const companyId = this.props.match.params.companyId
    if (companyId == null) {
      this.setState({ displayCompaniesTab: true })
      this.setActiveTab(true)
    } else {
      const companyDetails = await this.props.getCompany(companyId)
      const shouldDisplayCompanies = companyDetails.type == CompanyType.SERVICE

      this.setState({
        displayCompaniesTab: shouldDisplayCompanies,
        currentCompany: companyDetails,
      })
      this.setActiveTab(shouldDisplayCompanies)
    }
  }

  componentWillUnmount() {
    this.unsubscribeSelection?.()
    clearAdminSelection()
  }

  private setActiveTab(shouldDisplayCompanies: boolean) {
    let tab = this.props.match.params.tab
    if (tab == null) {
      this.navigateTab(shouldDisplayCompanies ? TABS.Company : TABS.User)
    } else {
      if (tab == TABS.Company && !shouldDisplayCompanies) {
        tab = TABS.User
      }
      this.trySetActiveTab(tab)
    }
  }

  private trySetActiveTab(tab) {
    if (
      (!isAdmin() && tab === TABS.Role) ||
      (this.state.currentCompany != null &&
        this.state.currentCompany.type == CompanyType.NORMAL &&
        (tab === TABS.Server || tab === TABS.Database))
    ) {
      this.props.history.push(ROUTES.NotFound)
      return
    }

    this.setState({ activeTab: tab })
  }

  async componentWillReceiveProps(
    nextProps: AdministrationPanelProps & RouteComponentProps<MatchParams>,
  ) {
    const tab = nextProps.match.params.tab
    if (tab !== this.state.activeTab) {
      clearAdminSelection()
      this.trySetActiveTab(tab)
    }

    const companyId = nextProps.match.params.companyId

    if (companyId == null) {
      this.setState({ currentCompany: null, displayCompaniesTab: true })
    } else if (companyId != this.props.match.params.companyId) {
      const companyDetails = await this.props.getCompany(companyId)
      const shouldDisplayCompanies = companyDetails.type == CompanyType.SERVICE

      this.setState({
        displayCompaniesTab: shouldDisplayCompanies,
        currentCompany: companyDetails,
      })
    }
  }

  navigateTab(tab) {
    const { companyId } = this.props.match.params
    const pattern = companyId
      ? ROUTES.AdministrationPanel.CompanyTab
      : ROUTES.AdministrationPanel.NoCompanyTab
    const url = generatePath(pattern, {
      tab,
      ...(companyId ? { companyId } : {}),
    })
    this.props.history.push(url)
  }

  toggle(tab) {
    if (this.state.activeTab !== tab) {
      this.navigateTab(tab)
    }
  }

  translate = (text: string): string => {
    const translateText = this.props.translate
    return translateText(text) as string
  }

  /**
   * Opis zakładek zamiast szesnastu powtórzonych bloków `NavItem`/`NavLink`.
   * `visible` liczone jest przy renderze, bo zależy od roli i od tego, czy panel
   * otwarto w kontekście firmy.
   */
  /** Adres innej zakładki panelu, z firmą w ścieżce albo bez niej. */
  /** Wspólne okno potwierdzenia zamiast własnego, pomarańczowego modala. */
  private async confirmRemoval() {
    const translate = this.translate
    const confirmed = await this.props.confirm({
      title: translate('administrationPanel.confirmation-popup-title'),
      message: translate('administrationPanel.confirmation-popup-message'),
      confirmText: translate('answer.yes'),
      cancelText: translate('answer.no'),
    })

    if (confirmed) {
      requestAdminRemove()
    }
  }

  private buildTabUrl(tab: string) {
    const companyId = this.props.match.params.companyId
    return (
      companyId
        ? ROUTES.AdministrationPanel.CompanyTab.replace(':companyId', companyId)
        : ROUTES.AdministrationPanel.NoCompanyTab
    ).replace(':tab', tab)
  }

  private getTabs() {
    const t = this.translate
    const company = this.state.currentCompany
    const noCompany = company == null
    const serviceCompany =
      company != null && company.type == CompanyType.SERVICE
    const adminOrService = (isAdmin() && noCompany) || serviceCompany
    const adminNoCompany = isAdmin() && noCompany

    return [
      {
        id: TABS.User,
        label: t('administrationPanel.users'),
        visible: (!isPaymentAdmin() && !isDemoCompanyAdmin()) || isAdmin(),
      },
      {
        id: TABS.Company,
        label: t('administrationPanel.companies'),
        visible:
          this.state.displayCompaniesTab &&
          ((!isPaymentAdmin() && !isDemoCompanyAdmin()) ||
            isAdmin() ||
            isCompanyAdmin()),
      },
      {
        id: TABS.Demo,
        label: t('administrationPanel.demo'),
        visible: isDemoCompanyAdmin() || isAdmin(),
      },
      {
        id: TABS.CompanyGroups,
        label: t('administrationPanel.company-groups'),
        visible:
          this.state.displayCompaniesTab &&
          ((!isPaymentAdmin() && !isDemoCompanyAdmin()) || isAdmin()),
      },
      {
        id: TABS.Database,
        label: t('administrationPanel.databases'),
        visible: adminOrService,
      },
      {
        id: TABS.Server,
        label: t('administrationPanel.servers'),
        visible: adminOrService,
      },
      {
        id: TABS.Role,
        label: t('administrationPanel.roles'),
        visible: adminNoCompany,
      },
      {
        id: TABS.Reports,
        label: t('administrationPanel.reports'),
        visible:
          ((isAdmin() || isPaymentAdmin()) && noCompany) || serviceCompany,
      },
      {
        id: TABS.Tasks,
        label: t('administrationPanel.tasks'),
        visible: adminNoCompany,
      },
      {
        id: TABS.Messages,
        label: t('administrationPanel.messages'),
        visible: adminNoCompany,
      },
      { id: TABS.System, label: 'System', visible: adminNoCompany },
      {
        id: TABS.PaymentCheck,
        label: 'Panel płatności',
        visible: adminNoCompany,
      },
      {
        id: TABS.ManualBilling,
        label: 'Rozliczenia ręczne',
        visible: adminNoCompany,
      },
      { id: TABS.Przelewy24, label: 'Przelewy24', visible: adminNoCompany },
      {
        id: TABS.ServiceAccount,
        label: 'Konta usługowe',
        visible: adminNoCompany,
      },
    ]
  }

  private renderTabs() {
    return (
      <div className="tw-sticky tw-top-0 tw-z-5 tw-flex tw-items-center tw-bg-content-primary">
        <div className="tw-min-w-0 tw-flex-1">
          <SubNavigationBar>
            {this.getTabs()
              .filter((tab) => tab.visible)
              .map((tab) => (
                <SubNavigationButton
                  key={tab.id}
                  isActive={this.state.activeTab === tab.id}
                  onClick={() => this.toggle(tab.id)}
                >
                  {tab.label}
                </SubNavigationButton>
              ))}
          </SubNavigationBar>
        </div>
        <div className="tw-flex-shrink-0 tw-px-3">
          <ThemeModeToggle />
        </div>
      </div>
    )
  }

  render() {
    const translate = this.translate
    return (
      <div>
        <div className="admin-panel-wrapper">
          {this.renderTabs()}
          {isAdmin() && this.props.match.params.companyId == null && (
            <CompanyNipMismatchBanner />
          )}
          <TabContent activeTab={this.state.activeTab}>
            <TabPane tabId={TABS.User}>
              {this.state.activeTab === TABS.User && (
                <AdminUsersPage
                  companyId={this.props.match.params.companyId}
                  onEditUser={(user) => {
                    const companyId = this.props.match.params.companyId
                    const url = companyId
                      ? ROUTES.AdministrationPanel.EditUser.replace(
                          ':companyId',
                          companyId,
                        ).replace(':userId', String(user.id))
                      : ROUTES.AdministrationPanel.EditUserNoCompany.replace(
                          ':userId',
                          String(user.id),
                        )
                    this.props.history.push(url)
                  }}
                />
              )}
            </TabPane>
            <TabPane tabId={TABS.Company}>
              {this.state.activeTab === TABS.Company && (
                <AdminCompaniesPage
                  currentCompanyId={this.props.match.params.companyId}
                  key={this.props.match.params.companyId}
                  currentCompany={this.state.currentCompany}
                />
              )}
            </TabPane>
            <TabPane tabId={TABS.Demo}>
              {this.state.activeTab === TABS.Demo && (
                <AdminDemoCompaniesPage
                  currentCompanyId={this.props.match.params.companyId}
                  onEditCompany={(company) =>
                    this.props.history.push(
                      ROUTES.AdministrationPanel.EditCompany.replace(
                        ':companyId',
                        company.id,
                      ),
                    )
                  }
                />
              )}
            </TabPane>
            <TabPane tabId={TABS.CompanyGroups}>
              {this.state.activeTab === TABS.CompanyGroups && (
                <AdminCompanyGroupsPage
                  currentCompanyId={this.props.match.params.companyId}
                />
              )}
            </TabPane>
            <TabPane tabId={TABS.Database}>
              {this.state.activeTab === TABS.Database && (
                <AdminDatabasesPage
                  currentCompanyId={this.props.match.params.companyId}
                  onGoToCompanies={(database) => {
                    const companyId = this.props.match.params.companyId
                    const url = (
                      companyId
                        ? ROUTES.AdministrationPanel.CompanyTab.replace(
                            ':companyId',
                            companyId,
                          )
                        : ROUTES.AdministrationPanel.NoCompanyTab
                    ).replace(':tab', `company?filterByDatabase=${database.id}`)
                    this.props.history.push(url)
                  }}
                />
              )}
            </TabPane>
            <TabPane tabId={TABS.Server}>
              {this.state.activeTab === TABS.Server && (
                <AdminServersPage
                  currentCompanyId={this.props.match.params.companyId}
                  onGoToDatabases={(server) =>
                    this.props.history.push(
                      this.buildTabUrl(`database?filterByServer=${server.id}`),
                    )
                  }
                  onGoToCompanies={(server) =>
                    this.props.history.push(
                      this.buildTabUrl(`company?filterByServer=${server.id}`),
                    )
                  }
                />
              )}
            </TabPane>
            <TabPane tabId={TABS.Role}>
              {this.state.activeTab === TABS.Role && <AdminRolesPage />}
            </TabPane>
            <TabPane tabId={TABS.Tasks}>
              {this.state.activeTab === TABS.Tasks && <AdminTasksPage />}
            </TabPane>
            <TabPane tabId={TABS.Reports}>
              {this.state.activeTab === TABS.Reports && (
                <Reports currentCompanyId={this.props.match.params.companyId} />
              )}
            </TabPane>
            <TabPane tabId={TABS.Messages}>
              {this.state.activeTab === TABS.Messages && <AdminMessagesPage />}
            </TabPane>
            <TabPane tabId={TABS.ManualBilling}>
              {this.state.activeTab === TABS.ManualBilling && (
                <ManualBillingTab />
              )}
            </TabPane>
            <TabPane tabId={TABS.Przelewy24}>
              {this.state.activeTab === TABS.Przelewy24 && (
                <Przelewy24BillingTab />
              )}
            </TabPane>
            <TabPane tabId={TABS.System}>
              {this.state.activeTab === TABS.System && <SystemTab />}
            </TabPane>
            <TabPane tabId={TABS.PaymentCheck}>
              {this.state.activeTab === TABS.PaymentCheck && (
                <PaymentCheckTab />
              )}
            </TabPane>
            <TabPane tabId={TABS.ServiceAccount}>
              {isAdmin() && this.state.activeTab === TABS.ServiceAccount && (
                <ServiceAccountTab />
              )}
            </TabPane>
          </TabContent>
        </div>
        {this.props.isLoading && this.state.activeTab != TABS.Server && (
          <div
            style={{
              width: '100%',
              height: '100%',
              zIndex: 100,
              background: '#fff',
              position: 'absolute',
              top: 50,
              left: 0,
              opacity: 0.85,
            }}
          >
            <FlexView
              vAlignContent="center"
              hAlignContent="center"
              className="no-data-wrapper"
              style={{ fontSize: 18, fontWeight: 500, color: '#fff' }}
            >
              <img
                src={`/assets/img/preloader.gif`}
                style={{ width: 128, height: 128 }}
                alt="icon"
              />
            </FlexView>
          </div>
        )}
        {this.state.displayActionsBar && (
          <div className="admin-panel-toolbar">
            <AdminIcon
              name="delete"
              className="white"
              title={translate('operation.delete')}
              onClick={() => this.confirmRemoval()}
            />
          </div>
        )}
      </div>
    )
  }
}

export default withConfirm(
  withRouter(withLocalizeProps(AdministrationPanel) as any) as any,
)
